/**
 * Générateur PDF/A-3 pour Factur-X
 * Combine un PDF visuel avec un XML EN 16931 embarqué
 *
 * IMPORTANT: Utilise le template HTML personnalisé de l'utilisateur
 * pour respecter l'identité visuelle (logo, couleurs, mise en page)
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { generateFacturXML, FacturXData } from './xmlGenerator';
import { addFacturXAttachment } from './pdfAttachment';
import { createFacturXPdfWithPdfLib } from './pdfLibAttachment';
import { toFrench } from '../dateUtils';

/**
 * Configuration des couleurs
 */
const COLORS = {
  primary: '#2563eb',
  secondary: '#64748b',
  text: '#1e293b',
  textLight: '#64748b',
  border: '#e2e8f0',
  background: '#f8fafc',
};

/**
 * Ajoute les métadonnées PDF/A-3 au document
 */
function addPDFAMetadata(pdf: jsPDF, data: FacturXData) {
  // Métadonnées XMP pour PDF/A-3
  const xmp = `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about=""
        xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/"
        xmlns:dc="http://purl.org/dc/elements/1.1/"
        xmlns:xmp="http://ns.adobe.com/xap/1.0/"
        xmlns:pdf="http://ns.adobe.com/pdf/1.3/"
        xmlns:pdfaExtension="http://www.aiim.org/pdfa/ns/extension/"
        xmlns:pdfaSchema="http://www.aiim.org/pdfa/ns/schema#"
        xmlns:pdfaProperty="http://www.aiim.org/pdfa/ns/property#"
        xmlns:fx="urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#">
      <pdfaid:part>3</pdfaid:part>
      <pdfaid:conformance>B</pdfaid:conformance>
      <dc:title>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">Facture ${data.invoiceNumber}</rdf:li>
        </rdf:Alt>
      </dc:title>
      <dc:creator>
        <rdf:Seq>
          <rdf:li>${data.seller.name}</rdf:li>
        </rdf:Seq>
      </dc:creator>
      <dc:description>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">Factur-X Invoice</rdf:li>
        </rdf:Alt>
      </dc:description>
      <xmp:CreatorTool>StartEvent Factur-X Generator</xmp:CreatorTool>
      <xmp:CreateDate>${new Date().toISOString()}</xmp:CreateDate>
      <xmp:ModifyDate>${new Date().toISOString()}</xmp:ModifyDate>
      <pdf:Producer>jsPDF with Factur-X extension</pdf:Producer>
      <fx:ConformanceLevel>MINIMUM</fx:ConformanceLevel>
      <fx:DocumentFileName>factur-x.xml</fx:DocumentFileName>
      <fx:DocumentType>INVOICE</fx:DocumentType>
      <fx:Version>1.0</fx:Version>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;

  // Ajouter les métadonnées au PDF
  // Note: jsPDF ne supporte pas nativement les métadonnées XMP complexes
  // On les stocke comme propriétés personnalisées
  pdf.setProperties({
    title: `Facture ${data.invoiceNumber}`,
    subject: 'Factur-X Invoice',
    author: data.seller.name,
    keywords: 'facture, invoice, factur-x, en16931',
    creator: 'StartEvent Factur-X Generator',
  });

  return xmp;
}


/**
 * Formate un montant en euros
 * Note: Remplace l'espace insécable par un espace normal pour le rendu PDF
 */
function formatEuro(amount: number): string {
  return amount.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).replace(/\u00A0/g, ' ');
}

/**
 * Formate une date
 */
function formatDate(date: Date): string {
  return toFrench(date);
}

/**
 * Génère un PDF Factur-X en capturant le template HTML personnalisé
 * Cette fonction utilise le template affiché à l'écran pour respecter
 * l'identité visuelle de l'utilisateur (logo, couleurs, mise en page)
 */
export async function generateFacturXPDF(data: FacturXData): Promise<Blob> {
  // Générer le XML EN 16931
  const xmlContent = generateFacturXML(data);

  // Masquer les boutons d'action avant la capture
  const actionButtons = document.querySelectorAll('.fixed.bottom-6');
  const printHiddenElements = document.querySelectorAll('.print\\:hidden');
  const elementsToHide = [...Array.from(actionButtons), ...Array.from(printHiddenElements)];

  elementsToHide.forEach(btn => {
    (btn as HTMLElement).style.display = 'none';
  });

  // Capturer le template HTML de la facture
  const element = document.getElementById('invoice-template');
  if (!element) {
    // Restaurer les éléments cachés
    elementsToHide.forEach(btn => {
      (btn as HTMLElement).style.display = '';
    });
    throw new Error('Template de facture non trouvé - impossible de générer le PDF Factur-X');
  }

  // Capturer le template avec html2canvas
  // Optimisations pour mobile: scale réduit, JPEG compression, viewport fixe
  const canvas = await html2canvas(element, {
    scale: 1.2, // Réduit de 2 à 1.2 pour des fichiers plus légers
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    windowWidth: 1024, // Force le viewport desktop même sur mobile
    width: element.scrollWidth,
    height: element.scrollHeight,
  });

  // Utiliser JPEG au lieu de PNG pour une meilleure compression
  const imgData = canvas.toDataURL('image/jpeg', 0.85);

  // Créer le PDF
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  // Ajouter les métadonnées PDF/A-3
  addPDFAMetadata(pdf, data);

  // Ajouter l'image capturée au PDF
  const imgWidth = 210; // A4 width in mm
  const pageHeight = 295; // A4 height in mm
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;

  // Ajouter la première page
  pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
  heightLeft -= pageHeight;

  // Ajouter des pages supplémentaires si nécessaire
  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
    heightLeft -= pageHeight;
  }

  // Restaurer les éléments cachés avant de finaliser le PDF
  elementsToHide.forEach(btn => {
    (btn as HTMLElement).style.display = '';
  });

  // Extraire le PDF jsPDF en ArrayBuffer
  const pdfArrayBuffer = pdf.output('arraybuffer');

  // Utiliser la nouvelle approche améliorée avec métadonnées PDF/A-3 complètes
  const { createFacturXPdfImproved } = await import('./pdfA3Generator');
  const facturxPdfBytes = await createFacturXPdfImproved(
    pdfArrayBuffer,
    xmlContent,
    data.invoiceNumber,
    data.seller.name
  );

  // Retourner le PDF Factur-X en tant que Blob
  return new Blob([facturxPdfBytes as BlobPart], { type: 'application/pdf' });
}

/**
 * Génère un PDF Factur-X avec un template générique (ancienne version)
 * DÉPRÉCIÉ: Utilisez generateFacturXPDF() qui capture le template HTML personnalisé
 */
export async function generateFacturXPDFLegacy(data: FacturXData): Promise<Blob> {
  // Générer le XML EN 16931
  const xmlContent = generateFacturXML(data);

  // Créer le PDF
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true, // Compression pour réduire la taille
  });

  // Ajouter les métadonnées PDF/A-3
  addPDFAMetadata(pdf, data);

  // Embarquer le XML Factur-X dans le PDF
  addFacturXAttachment(pdf, xmlContent);

  // === En-tête de la facture ===
  let yPos = 20;

  // Logo et informations vendeur
  pdf.setFontSize(20);
  pdf.setTextColor(COLORS.primary);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.seller.name, 20, yPos);

  yPos += 10;
  pdf.setFontSize(9);
  pdf.setTextColor(COLORS.textLight);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.seller.address, 20, yPos);
  yPos += 5;
  pdf.text(`${data.seller.postalCode} ${data.seller.city}`, 20, yPos);
  yPos += 5;
  if (data.seller.phone) {
    pdf.text(`Tél: ${data.seller.phone}`, 20, yPos);
    yPos += 5;
  }
  if (data.seller.email) {
    pdf.text(`Email: ${data.seller.email}`, 20, yPos);
    yPos += 5;
  }

  // Numéro de facture et date (à droite)
  pdf.setFontSize(12);
  pdf.setTextColor(COLORS.text);
  pdf.setFont('helvetica', 'bold');
  pdf.text('FACTURE', 150, 20);

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`N° ${data.invoiceNumber}`, 150, 27);
  pdf.text(`Date: ${formatDate(data.invoiceDate)}`, 150, 33);
  if (data.dueDate) {
    pdf.text(`Échéance: ${formatDate(data.dueDate)}`, 150, 39);
  }

  // === Informations client ===
  yPos = 60;
  pdf.setFillColor(COLORS.background);
  pdf.rect(20, yPos, 80, 35, 'F');

  yPos += 7;
  pdf.setFontSize(10);
  pdf.setTextColor(COLORS.text);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Client', 25, yPos);

  yPos += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(data.buyer.name, 25, yPos);
  yPos += 5;
  pdf.text(data.buyer.address, 25, yPos);
  yPos += 5;
  pdf.text(`${data.buyer.postalCode} ${data.buyer.city}`, 25, yPos);
  yPos += 5;
  if (data.buyer.vatNumber) {
    pdf.text(`TVA: ${data.buyer.vatNumber}`, 25, yPos);
  }

  // === Tableau des articles ===
  yPos = 110;

  // En-tête du tableau
  pdf.setFillColor(COLORS.primary);
  pdf.rect(20, yPos, 170, 8, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.text('Description', 22, yPos + 5.5);
  pdf.text('Qté', 120, yPos + 5.5);
  pdf.text('P.U. HT', 135, yPos + 5.5);
  pdf.text('TVA', 155, yPos + 5.5);
  pdf.text('Total HT', 170, yPos + 5.5);

  yPos += 8;

  // Lignes du tableau
  pdf.setTextColor(COLORS.text);
  pdf.setFont('helvetica', 'normal');

  data.items.forEach((item, index) => {
    // Alterner les couleurs de fond
    if (index % 2 === 0) {
      pdf.setFillColor(COLORS.background);
      pdf.rect(20, yPos, 170, 7, 'F');
    }

    pdf.text(item.description, 22, yPos + 5);
    pdf.text(item.quantity.toString(), 120, yPos + 5);
    pdf.text(formatEuro(item.unitPrice), 135, yPos + 5);
    pdf.text(`${item.taxRate}%`, 155, yPos + 5);
    pdf.text(formatEuro(item.total), 170, yPos + 5);

    yPos += 7;

    // Nouvelle page si nécessaire
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }
  });

  // Ligne de séparation
  pdf.setDrawColor(COLORS.border);
  pdf.line(20, yPos, 190, yPos);
  yPos += 10;

  // === Totaux ===
  const subtotal = data.items.reduce((sum, item) => sum + item.total, 0);
  const subtotalAfterDiscount = subtotal - (data.discountAmount || 0);

  // Calculer les totaux de TVA par taux
  const taxGroups: { [rate: number]: { base: number; amount: number } } = {};
  data.items.forEach(item => {
    if (!taxGroups[item.taxRate]) {
      taxGroups[item.taxRate] = { base: 0, amount: 0 };
    }
    taxGroups[item.taxRate].base += item.total;
  });

  // Appliquer la remise proportionnellement
  if (data.discountAmount && data.discountAmount > 0) {
    Object.keys(taxGroups).forEach(rate => {
      const rateNum = parseFloat(rate);
      const proportion = taxGroups[rateNum].base / subtotal;
      taxGroups[rateNum].base -= (data.discountAmount || 0) * proportion;
    });
  }

  // Calculer les montants de TVA
  Object.keys(taxGroups).forEach(rate => {
    const rateNum = parseFloat(rate);
    taxGroups[rateNum].amount = (taxGroups[rateNum].base * rateNum) / 100;
  });

  const totalTax = Object.values(taxGroups).reduce((sum, g) => sum + g.amount, 0);
  const total = subtotalAfterDiscount + totalTax;

  // Afficher les totaux
  const totalsX = 130;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);

  pdf.text('Total HT:', totalsX, yPos);
  pdf.text(formatEuro(subtotal), 175, yPos, { align: 'right' });
  yPos += 6;

  // Remise si applicable
  if (data.discountAmount && data.discountAmount > 0) {
    pdf.text(`Remise${data.discountReason ? ` (${data.discountReason})` : ''}:`, totalsX, yPos);
    pdf.text(`-${formatEuro(data.discountAmount)}`, 175, yPos, { align: 'right' });
    yPos += 6;

    pdf.text('Total HT après remise:', totalsX, yPos);
    pdf.text(formatEuro(subtotalAfterDiscount), 175, yPos, { align: 'right' });
    yPos += 6;
  }

  // Détail TVA par taux
  Object.entries(taxGroups).forEach(([rate, amounts]) => {
    pdf.text(`TVA ${rate}%:`, totalsX, yPos);
    pdf.text(formatEuro(amounts.amount), 175, yPos, { align: 'right' });
    yPos += 6;
  });

  // Total TTC
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('Total TTC:', totalsX, yPos);
  pdf.text(formatEuro(total), 175, yPos, { align: 'right' });

  // === Mentions légales ===
  yPos += 15;

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(COLORS.textLight);

  // Informations légales vendeur
  const legalLines = [
    `SIRET: ${data.seller.siret} - TVA: ${data.seller.vatNumber}`,
  ];

  if (data.seller.iban) {
    legalLines.push(`IBAN: ${data.seller.iban}${data.seller.bic ? ` - BIC: ${data.seller.bic}` : ''}`);
  }

  if (data.paymentTerms) {
    legalLines.push(`Conditions de paiement: ${data.paymentTerms}`);
  }

  // Mentions légales obligatoires françaises
  legalLines.push('En cas de retard de paiement, application d\'une pénalité égale à 3 fois le taux d\'intérêt légal.');
  legalLines.push('Indemnité forfaitaire pour frais de recouvrement en cas de retard de paiement: 40€.');

  if (data.legalMentions) {
    legalLines.push(data.legalMentions);
  }

  legalLines.forEach(line => {
    if (yPos > 280) {
      pdf.addPage();
      yPos = 20;
    }
    pdf.text(line, 20, yPos, { maxWidth: 170 });
    yPos += 5;
  });

  // Notes supplémentaires
  if (data.notes) {
    yPos += 5;
    pdf.setFont('helvetica', 'italic');
    pdf.text(`Note: ${data.notes}`, 20, yPos, { maxWidth: 170 });
  }

  // Indication Factur-X
  pdf.setFontSize(7);
  pdf.setTextColor(COLORS.primary);
  yPos = 287;
  pdf.text('✓ Document Factur-X conforme EN 16931 (données structurées embarquées)', 105, yPos, { align: 'center' });

  // Retourner le PDF en tant que Blob
  return pdf.output('blob');
}

/**
 * Télécharge un PDF Factur-X
 */
export async function downloadFacturXPDF(data: FacturXData) {
  const pdfBlob = await generateFacturXPDF(data);
  const url = URL.createObjectURL(pdfBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Facture_${data.invoiceNumber}_${data.buyer.name.replace(/\s+/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Obtient le XML embarqué d'un PDF Factur-X généré
 * Utile pour la validation et les tests
 */
export { getFacturXContent as getEmbeddedXML } from './pdfAttachment';
